import type { RoomDep, RoomDepDependencies } from "./room.types";
import { makeRoomState } from "./roomState/roomState";
import { makeRoomConnection } from "./roomConnection/roomConnection";
import { makeRoomActions } from "./roomActions/roomActions";
import { makeRoomProxyManager } from "./roomProxy/roomProxyManager";
import type { CloudRoomProxyEnv } from "./roomProxy/cloudRoomProxy";
import { getOrGenerateClientId } from "../utils/clientId";

export interface RoomDepConfig {
  workerUrl: string;
  /**
   * Optional client id override.
   *
   * This is useful for headless Node tests / scripts that want multiple independent
   * clients in the same process.
   */
  clientId?: string;
  /** Node-only: injects a `ws`-backed WebSocket for a headless client. */
  env?: CloudRoomProxyEnv;
}

export function makeRoomDep(deps: RoomDepDependencies, config: RoomDepConfig): RoomDep {
  const { workerUrl, env } = config;
  const { gameRegistry, gameCatalog, moveHistory } = deps
  const clientId = typeof config.clientId === 'string' ? config.clientId : getOrGenerateClientId();

  // Create RoomProxyManager
  const roomProxyManager = makeRoomProxyManager(gameCatalog, deps.auth);

  // 1. Create shared state
  // RoomState doesn't need external dependencies for now
  const stateInternal = makeRoomState({}, clientId);

  // 2. Create connection module
  // Connection needs RoomProxyManager and Auth
  const connection = makeRoomConnection({ roomProxyManager, moveHistory, auth: deps.auth, gameRegistry: gameRegistry as any }, stateInternal, { workerUrl, clientId, env });

  // 3. Create actions module
  // Actions need RoomProxyManager
  const actions = makeRoomActions({ roomProxyManager, moveHistory }, stateInternal, { clientId });

  // 4. Expose public state
  const state = {
    roomName: stateInternal.roomName,
    ownerName: stateInternal.ownerName,
    imageUrl: stateInternal.imageUrl,
    ownerId: stateInternal.ownerId,
    videoEnabled: stateInternal.videoEnabled,
    isClosed: stateInternal.isClosed,
    opensAt: stateInternal.opensAt,
    gameless: stateInternal.gameless,
    hostMateIds: stateInternal.hostMateIds,
    managed: stateInternal.managed,
    hostTerm: stateInternal.hostTerm,
    hostPlaysByGameId: stateInternal.hostPlaysByGameId,
    actingOmniMateId: stateInternal.actingOmniMateId,
    curated: stateInternal.curated,
    curatedGameIds: stateInternal.curatedGameIds,
    localHostMateId: stateInternal.localHostMateId,
    isLocalHost: stateInternal.isLocalHost,
    canControlGames: stateInternal.canControlGames,
    activeGame: stateInternal.activeGame,
    mates: stateInternal.mates,
    localMates: stateInternal.localMates,
    localAIPlayerIDs: stateInternal.localAIPlayerIDs,
    hasOmniMates: stateInternal.hasOmniMates,
    isLoading: stateInternal.isLoading,
    gameSetup: stateInternal.gameSetup,
    setupReadiness: stateInternal.setupReadiness,
    savedOptions: stateInternal.savedOptions,
    gameChannelPreference: stateInternal.gameChannelPreference,
    getPlayerIdForSlot: (slotIndex: number): string | undefined => {
      const game = stateInternal.activeGame.value
      return game?.playerIds?.[slotIndex]
    },
  };

  return {
    connection,
    state,
    actions,
    // Expose internal dependencies if needed, or keep them hidden
    _internal: {
      roomProxyManager
    }
  } as RoomDep & { _internal: { roomProxyManager: any } };
}
